<template>
    <div>
        <p class="leading-tight w-full mb-20"><span class="text-14">{{metricLabel}}</span><br><span class="font-bold text-26 md:text-56">{{thisMonthMetric}}</span></p>
        <p class="text-14 mb-20 -mt-20" v-html="monthOnMonthDifference"></p>
        <p class="leading-tight w-full mb-20"><span class="text-14">Last month</span><br><span class="text-18 md:text-26">{{lastMonthMetric}}</span></p>
        <p v-if="hasTargets" class="leading-tight w-full"><span class="text-14">Target</span><br><span class="text-18 md:text-26">{{targetMetric}}</span> <span class="text-14" v-html="targetDifference"></span></p>
    </div>
</template>

<script>

    import { format } from "date-fns"

    export default {

        props: {
            chartData: {
                type: Object,
                required: true,
            },

            scheme: {
                type: String,
                default: 'dark'
            },
        },

        computed: {
            decreasedColor() {
                return this.chartData.isInverted ? '#53ff4d' : '#ff5d67'
            },

            hasTargets() {
                let targets = this.chartData.datasets.filter(dataset => dataset.target)

                if (targets.length === 0) {
                    return false
                }

                return targets[0].data.filter(d => d !== null).length > 0
            },

            increasedColor() {
                return this.chartData.isInverted ? '#ff5d67' : '#53ff4d'
            },

            lastMonthMetric() {
                let length = this.chartData.datasets[0].data.length
                let lastMonth = length > 1 ? length -2 : 0
                let rawData = this.chartData.datasets[0].data[lastMonth]

                return rawData.toLocaleString()
            },

            metricLabel() {
                return this.chartData.datasets[0].label
            },

            monthOnMonthDifference() {

                let thisMonth = this.chartData.datasets[0].data.length - 1
                thisMonth = this.chartData.datasets[0].data[thisMonth]

                let length = this.chartData.datasets[0].data.length
                let lastMonth = length > 1 ? length -2 : 0
                lastMonth = this.chartData.datasets[0].data[lastMonth]

                let increased = false
                let percentageChange = null

                if (lastMonth === 0 && thisMonth > 0) {
                    increased = true
                }

                if (thisMonth === 0 && lastMonth > 0) {
                    increased = false
                }

                if (thisMonth > 0 && lastMonth > 0) {
                    percentageChange = 100 - (Math.round((lastMonth / thisMonth) * 100))
                    increased = percentageChange > 0 ? true : false
                }

                if (lastMonth === 0 && thisMonth === 0) {
                    percentageChange = 0
                }

                if (increased) {
                    if (this.viewingThisMonth) {
                        if (percentageChange === null) {
                            return `${this.chartData.isInverted ? 'greater then' : 'beaten'} last month! <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(-33.25 12.879) rotate(-90)" fill="${this.increasedColor}"/></svg>`
                        }

                        return `${this.chartData.isInverted ? 'greater then' : 'beaten'} last month by ${percentageChange}% already! <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(-33.25 12.879) rotate(-90)" fill="${this.increasedColor}"/></svg>`
                    }

                    if (percentageChange === null) {
                        return `up on last month! <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(-33.25 12.879) rotate(-90)" fill="${this.increasedColor}"/></svg>`
                    }

                    return `up ${percentageChange}% month on month <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(-33.25 12.879) rotate(-90)" fill="${this.increasedColor}"/></svg>`
                }

                if (percentageChange === 0) {
                    return `we've equaled last month <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(-33.25 12.879) rotate(-90)" fill="${this.decreasedColor}"/></svg>`
                }

                if (percentageChange === null) {
                    return `down on last month <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(43.25) rotate(90)" fill="${this.decreasedColor}"/></svg>`
                }

                return `down ${Math.abs(percentageChange)}% month on month <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(43.25) rotate(90)" fill="${this.decreasedColor}"/></svg>`
            },

            targetDifference() {
                let thisMonth = this.chartData.datasets[0].data.length - 1
                thisMonth = this.chartData.datasets[0].data[thisMonth]

                let target = this.chartData.datasets.find(dataset => dataset.target)
                let index = target.data.length > 0 ? target.data.length - 1 : 0
                let currenTarget = target.data[index] || null

                let percentageChange = 0

                if (currenTarget === null) {
                    return
                }

                if (thisMonth > 0 && currenTarget > 0) {
                    percentageChange = Math.round((thisMonth / currenTarget) * 100)
                }

                if (currenTarget === 0 && thisMonth === 0) {
                    percentageChange = 100
                }

                if (percentageChange === 100) {
                    return `we ${this.chartData.isInverted ? 'equaled' : 'hit'} our target ${this.chartData.isInverted ? '' : '100%!'}`
                }

                if (this.viewingThisMonth) {
                    if (percentageChange > 100) {
                        return `we've ${this.chartData.isInverted ? 'reached' : 'achieved'} ${percentageChange}% already! <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(-33.25 12.879) rotate(-90)" fill="${this.increasedColor}"/></svg>`
                    }

                    return `we're ${percentageChange}% ${this.chartData.isInverted ? '' : 'there'}`
                }

                if (percentageChange > 100) {
                    return `we ${this.chartData.isInverted ? 'reached' : 'achieved'} ${percentageChange}% <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(-33.25 12.879) rotate(-90)" fill="${this.increasedColor}"/></svg>`
                }

                return `we achieved ${percentageChange}% <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12.879" viewBox="0 0 10 12.879" class="inline-block ml-2 mb-4"><path d="M12.879,38.228,6.407,33.25v2.9H0v4.286H6.407V43.25Z" transform="translate(43.25) rotate(90)" fill="${this.increasedColor}"/></svg>`
            },

            targetMetric() {
                let target = this.chartData.datasets.find(dataset => dataset.target)
                let index = target.data.length > 0 ? target.data.length - 1 : 0

                return target.data[index].toLocaleString()
            },

            thisMonthMetric() {
                let thisMonth = this.chartData.datasets[0].data.length - 1
                let rawData = this.chartData.datasets[0].data[thisMonth]

                return rawData.toLocaleString()
            },

            viewingThisMonth() {
                let index = this.chartData.labels.findIndex(label => {
                    return label === format(new Date(), 'MMM')
                })
                return index !== -1
            }
        },

    }
</script>